import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Bold, Italic, Underline as UnderlineIcon, List, Strikethrough, Undo2, Redo2 } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { markdownToHtml, sanitizeHtml, execFormat } from "@/lib/richtext";
import { cn } from "@/lib/utils";

/**
 * WYSIWYG editor built on contenteditable.
 * - value: HTML string (or legacy markdown — auto-converted)
 * - onChange(nextHtml): fires with sanitized HTML on every edit
 */
const RichTextEditor = forwardRef(function RichTextEditor(
  { value, onChange, placeholder = "", testid = "richtext", minHeight = "200px" },
  ref
) {
  const { t } = useT();
  const edRef = useRef(null);
  // Lint-stable key to avoid resetting innerHTML on every parent render.
  const lastValueRef = useRef("");
  // Which format commands are currently active at the caret. Lets the toolbar
  // light up (and the bold "B" render extra-black) while the user types.
  const [activeFmt, setActiveFmt] = useState({ bold: false, italic: false, underline: false, strikeThrough: false, insertUnorderedList: false });

  useImperativeHandle(ref, () => ({
    focus: () => edRef.current?.focus(),
    getHtml: () => edRef.current?.innerHTML || "",
  }), []);

  useEffect(() => {
    const el = edRef.current;
    if (!el) return;
    const incoming = value || "";
    if (incoming === lastValueRef.current) return;
    const html = sanitizeHtml(markdownToHtml(incoming));
    el.innerHTML = html;
    lastValueRef.current = incoming;
  }, [value]);

  const refreshActive = useCallback(() => {
    if (typeof document === "undefined") return;
    try {
      setActiveFmt({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikeThrough: document.queryCommandState("strikeThrough"),
        insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      });
    } catch {}
  }, []);

  // Keep toolbar state in sync with caret/selection changes.
  useEffect(() => {
    const onSel = () => {
      const sel = window.getSelection();
      if (!sel || !edRef.current) return;
      if (sel.rangeCount && edRef.current.contains(sel.anchorNode)) {
        refreshActive();
      }
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, [refreshActive]);

  const emit = () => {
    const html = sanitizeHtml(edRef.current?.innerHTML || "");
    lastValueRef.current = html;
    onChange?.(html);
    refreshActive();
  };

  const run = (cmd) => {
    edRef.current?.focus();
    execFormat(cmd);
    emit();
  };

  const onPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1 pb-1" data-testid={`${testid}-toolbar`}>
        <ToolbarBtn onClick={() => run("undo")} label="Undo" testid="toolbar-undo">
          <Undo2 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => run("redo")} label="Redo" testid="toolbar-redo">
          <Redo2 className="h-4 w-4" />
        </ToolbarBtn>
        <div className="w-px h-5 bg-stone-200 dark:bg-stone-700 mx-1" />
        <ToolbarBtn
          onClick={() => run("bold")}
          label={t("editorToolbar.bold")}
          testid="toolbar-bold"
          active={activeFmt.bold}
        >
          {/* Icon gets its own stronger stroke when bold is active so users see
              the "B" render visually darker/thicker during live typing. */}
          <Bold className={cn("h-4 w-4", activeFmt.bold && "[stroke-width:3]")} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => run("italic")} label={t("editorToolbar.italic")} testid="toolbar-italic" active={activeFmt.italic}>
          <Italic className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => run("underline")} label="Underline" testid="toolbar-underline" active={activeFmt.underline}>
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => run("strikeThrough")} label="Strikethrough" testid="toolbar-strike" active={activeFmt.strikeThrough}>
          <Strikethrough className="h-4 w-4" />
        </ToolbarBtn>
        <div className="w-px h-5 bg-stone-200 dark:bg-stone-700 mx-1" />
        <ToolbarBtn
          onClick={() => run("insertUnorderedList")}
          label={t("editorToolbar.bullet")}
          testid="toolbar-dot"
          active={activeFmt.insertUnorderedList}
        >
          <List className="h-4 w-4" />
        </ToolbarBtn>
      </div>
      <div
        ref={edRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
        data-testid={`${testid}-content`}
        data-placeholder={placeholder}
        className="richtext-content outline-none text-base leading-relaxed bg-transparent px-0 min-h-[200px] whitespace-pre-wrap break-words"
        style={{ minHeight }}
      />
    </div>
  );
});

function ToolbarBtn({ children, onClick, label, testid, active }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}  // keep selection
      onClick={onClick}
      aria-label={label}
      aria-pressed={!!active}
      title={label}
      data-testid={testid}
      data-active={active ? "true" : undefined}
      className={cn(
        "h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors",
        active
          ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 shadow-sm"
          : "text-stone-700 dark:text-stone-200 hover:bg-stone-200/70 dark:hover:bg-stone-700/70"
      )}
    >
      {children}
    </button>
  );
}

export default RichTextEditor;
